import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import AnimatedSection from '../components/common/AnimatedSection';
import SEOHead from '../components/common/SEOHead';
import { accessories, tires } from '../data/mock';

const AccessoriesPage = () => {
  const [activeTab, setActiveTab] = useState('todos');

  const filteredItems = useMemo(() => {
    if (activeTab === 'todos') return [...accessories, ...tires];
    if (activeTab === 'pneus') return tires;
    return accessories;
  }, [activeTab]);

  return (
    <>
      <SEOHead 
        title="Pneus e Acessórios"
        description="Pneus com câmara, pneus maciços, deslocadores laterais, posicionadores de garfos, garras para papel e muito mais."
        keywords="pneus empilhadeira, acessórios empilhadeira, deslocador lateral, garra papel, garfo giratório"
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-teal-900/30 py-20">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fadeUp">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Pneus e Acessórios</h1>
            <p className="text-xl text-gray-400">Tudo para complementar sua empilhadeira</p>
          </AnimatedSection>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4">
          {/* Tabs */}
          <AnimatedSection animation="fadeUp" className="mb-8">
            <div className="flex flex-wrap gap-4 justify-center">
              {[
                { id: 'todos', label: 'Todos' },
                { id: 'pneus', label: 'Pneus' },
                { id: 'acessorios', label: 'Acessórios' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                    activeTab === tab.id
                      ? 'bg-teal-500 text-white shadow-lg shadow-teal-500/30'
                      : 'bg-white text-gray-700 hover:bg-teal-50 border border-gray-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </AnimatedSection>

          {/* Products Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredItems.map((item, index) => (
              <AnimatedSection key={item.id} animation="fadeUp" delay={index * 100}>
                <div className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-gray-100 hover:border-teal-200 transition-all duration-500">
                  <div className="relative overflow-hidden">
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className="w-full h-52 object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute top-4 left-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        item.type ? 'bg-blue-500 text-white' : 'bg-teal-500 text-white'
                      }`}>
                        {item.type ? 'Pneu' : 'Acessório'}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                      {item.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">{item.description}</p>
                    
                    {item.capacity && (
                      <p className="text-sm text-gray-500 mb-2">
                        <span className="font-medium">Capacidade:</span> {item.capacity}
                      </p>
                    )}
                    {item.model && (
                      <p className="text-sm text-gray-500 mb-2">
                        <span className="font-medium">Modelo:</span> {item.model}
                      </p>
                    )}
                    {item.quality && (
                      <p className="text-sm text-gray-500 mb-2">
                        <span className="font-medium">Qualidade:</span> {item.quality}
                      </p>
                    )}
                    
                    {item.features && (
                      <ul className="mt-4 space-y-1">
                        {item.features.slice(0, 3).map((feature, i) => (
                          <li key={i} className="text-sm text-gray-500 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-teal-500 rounded-full"></span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>

          {/* CTA */}
          <AnimatedSection animation="fadeUp" className="text-center mt-16">
            <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-2xl p-8 md:p-12">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Não encontrou o que procura?
              </h2>
              <p className="text-teal-100 mb-6">
                Entre em contato conosco e solicite um orçamento personalizado.
              </p>
              <Link
                to="/contato"
                className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-xl font-bold hover:bg-gray-100 hover:scale-105 transition-all duration-300"
              >
                Solicitar Orçamento
                <ChevronRight size={20} />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
};

export default AccessoriesPage;
